import { LitElement, html, css, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";

/**
 * The closed tag palette. Each value maps to a `--bino-tag-<color>-{bg,fg}` pair
 * in shared/tokens.css, and the server validates the same eight strings
 * (migrations/0044_workspace_tags.go).
 *
 * It lives here rather than in api/tags.ts because every file in components/
 * imports nothing but lit — api/tags.ts aliases this with a type-only import,
 * which erases at build, so the wire enum and the rendered swatches cannot drift.
 */
export type BnTagColor =
  | "gray"
  | "celeste"
  | "yellow"
  | "magenta"
  | "green"
  | "red"
  | "blue"
  | "purple";

/** TAG_COLORS is the palette in swatch-grid order: neutral first, then hues. */
export const TAG_COLORS: readonly BnTagColor[] = [
  "gray",
  "celeste",
  "green",
  "blue",
  "purple",
  "magenta",
  "red",
  "yellow",
];

/**
 * bn-tag is a workspace tag chip. It is deliberately NOT a bn-badge variant:
 * BnBadgeVariant is a semantic *state* enum (neutral/info/success/warning/error)
 * fed by buildStatusVariant, roleVariant, liveStatusVariant and pullVariant, and
 * widening it to thirteen members would let those mappers legally return
 * "purple" while `<bn-badge variant="green">` would say nothing about state.
 *
 * Three forms, one element:
 *
 *   <bn-tag .name=${t.name} color=${t.color}></bn-tag>              read-only
 *   <bn-tag removable @remove=${...}>                               detach
 *   <bn-tag selectable ?selected=${on} @toggle=${...}>              filter
 *
 * The read-only form renders a plain <span>, so it is safe inside the <a> of a
 * workspace card — the other two would be nested interactive content there.
 */
@customElement("bn-tag")
export class BnTag extends LitElement {
  @property() color: BnTagColor = "gray";
  /**
   * The tag's name. A property rather than a slot (which is how bn-badge takes
   * its label) because the chip needs the text itself to build the remove
   * control's accessible name.
   */
  @property() name = "";
  /** Renders the chip as a toggle button emitting `toggle` with `{selected}`. */
  @property({ type: Boolean }) selectable = false;
  @property({ type: Boolean }) selected = false;
  /**
   * Renders a trailing × emitting `remove`. Ignored when `selectable` is set —
   * the two together would nest a button inside a button.
   */
  @property({ type: Boolean }) removable = false;
  @property({ type: Boolean }) disabled = false;

  static override styles = css`
    :host {
      display: inline-flex;
      max-width: 100%;
    }
    /* Base pill matches bn-badge so a tag and a status badge sit on the same
       optical line when they share a row. */
    .pill {
      display: inline-flex;
      align-items: center;
      gap: var(--bino-space-xs);
      padding: 0.125rem 0.5rem;
      border-radius: var(--bino-radius-pill);
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-sm);
      font-weight: 600;
      line-height: 1.4;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    button.pill {
      border: 1px solid transparent;
      cursor: pointer;
      font: inherit;
      font-weight: 600;
    }
    button.pill[aria-pressed="true"] {
      border-color: currentColor;
    }
    button.pill:focus-visible,
    .x:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px var(--bino-focus-ring);
    }
    button.pill:disabled,
    .x:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }
    /* Fixed width so toggling the check on and off does not reflow the row. */
    .check {
      flex: none;
      width: 0.75em;
      text-align: center;
    }
    .pill.removable {
      padding-right: 0.25rem;
    }
    /* Lifted from the selected-entity chip in pages/page-admin-teams.ts. */
    .x {
      flex: none;
      background: none;
      border: none;
      cursor: pointer;
      color: inherit;
      font-size: 14px;
      line-height: 1;
      padding: 2px;
      border-radius: 50%;
    }
    .x:hover:not(:disabled) {
      background: rgba(10, 13, 15, 0.08);
    }
    .gray {
      background: var(--bino-tag-gray-bg);
      color: var(--bino-tag-gray-fg);
    }
    .celeste {
      background: var(--bino-tag-celeste-bg);
      color: var(--bino-tag-celeste-fg);
    }
    .yellow {
      background: var(--bino-tag-yellow-bg);
      color: var(--bino-tag-yellow-fg);
    }
    .magenta {
      background: var(--bino-tag-magenta-bg);
      color: var(--bino-tag-magenta-fg);
    }
    .green {
      background: var(--bino-tag-green-bg);
      color: var(--bino-tag-green-fg);
    }
    .red {
      background: var(--bino-tag-red-bg);
      color: var(--bino-tag-red-fg);
    }
    .blue {
      background: var(--bino-tag-blue-bg);
      color: var(--bino-tag-blue-fg);
    }
    .purple {
      background: var(--bino-tag-purple-bg);
      color: var(--bino-tag-purple-fg);
    }
  `;

  private onToggle(e: Event) {
    // The native click is composed, so it would escape the shadow root and reach
    // the page's own @click alongside the CustomEvent below. Stop it at the
    // boundary so consumers only ever see `toggle` — the same rule bn-input
    // applies to `input`.
    e.stopPropagation();
    this.selected = !this.selected;
    this.dispatchEvent(
      new CustomEvent("toggle", {
        detail: { selected: this.selected },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private onRemove(e: Event) {
    e.stopPropagation();
    this.dispatchEvent(new CustomEvent("remove", { bubbles: true, composed: true }));
  }

  override render() {
    if (this.selectable) {
      return html`<button
        class="pill ${this.color}"
        type="button"
        aria-pressed=${this.selected ? "true" : "false"}
        ?disabled=${this.disabled}
        @click=${this.onToggle}
      >
        <span class="check" aria-hidden="true">${this.selected ? "✓" : ""}</span>${this.name}
      </button>`;
    }
    return html`<span class="pill ${this.color} ${this.removable ? "removable" : ""}"
      >${this.name}${this.removable
        ? html`<button
            class="x"
            type="button"
            title=${`Remove ${this.name}`}
            aria-label=${`Remove tag ${this.name}`}
            ?disabled=${this.disabled}
            @click=${this.onRemove}
          >
            ×
          </button>`
        : nothing}</span
    >`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bn-tag": BnTag;
  }
}
